import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import {
  FiArrowLeft, FiLogIn, FiMaximize, FiMinimize, FiSettings,
  FiVolume2, FiVolumeX, FiZap, FiX,
} from 'react-icons/fi';
import './PowerHourPage.css';

interface RepRow {
  name: string;
  dials: number;
  connects: number;
  meetings: number;
  coins: number;
  streak?: number;
  last_dial_at: string | null;
}

interface SessionCfg {
  name: string;
  start_time: string;
  duration_minutes: number;
  meeting_target: number;
}

interface LivePayload {
  code?: string;
  session: SessionCfg | null;
  server_time?: string;
  reps: RepRow[];
  team?: { dials: number; connects: number; meetings: number; coins: number };
  events: { type: string; rep: string; at: string; source?: string }[];
  sources?: {
    nooks_calls: number;
    gong_calls: number;
    nooks_webhook_configured: boolean;
    webhook_last_secs?: number | null;
  };
  gong_error?: string | null;
  error?: string;
}

interface LobbySession extends SessionCfg { code: string; }

interface HistoryBucket {
  period: string;
  dials: number;
  connects: number;
  meetings: number;
  coins: number;
  reps: { name: string; dials: number; connects: number; meetings: number; coins: number }[];
}

const DEFAULT_TARGETS: Record<string, number> = { day: 5, week: 25, month: 100 };

const POLL_MS = 5000;
const ROW_H = 64;

const MEDALS = ['🥇', '🥈', '🥉'];
const HYPE = [
  'COFFEE IS FOR CLOSERS ☕', 'SMILE AND DIAL 📞', 'ABC — ALWAYS BE CLOSING',
  'PUT THAT COFFEE DOWN', 'THE LEADS ARE STRONG 💪', 'RING RING RING 🔔',
];

const adminKeyFor = (code: string) => localStorage.getItem(`ph-admin-${code.toUpperCase()}`) || '';

// Date -> value for <input type="datetime-local"> in the user's local tz
const toLocalInput = (d: Date) => {
  const x = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return x.toISOString().slice(0, 16);
};

const initials = (name: string) =>
  name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

// deterministic vivid color per rep
const repHue = (name: string) => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) % 360;
  return h;
};

const pad = (n: number) => String(Math.max(0, n)).padStart(2, '0');

const fmtClock = (ms: number) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(sec)}` : `${pad(m)}:${pad(sec)}`;
};

// --- tiny synth for hype sounds (no assets needed) ---
let audioCtx: AudioContext | null = null;
const playTone = (freqs: number[], dur = 0.12, type: OscillatorType = 'square', gain = 0.06) => {
  try {
    if (!audioCtx) audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const ctx = audioCtx;
    freqs.forEach((f, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = type;
      o.frequency.value = f;
      g.gain.setValueAtTime(gain, ctx.currentTime + i * dur);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + (i + 1) * dur);
      o.connect(g).connect(ctx.destination);
      o.start(ctx.currentTime + i * dur);
      o.stop(ctx.currentTime + (i + 1) * dur + 0.02);
    });
  } catch { /* audio is best-effort */ }
};
const sndCoin = () => playTone([988, 1319], 0.09);
const sndMeeting = () => playTone([523, 659, 784, 1047, 1319], 0.11, 'triangle', 0.09);
const sndRankUp = () => playTone([440, 554, 659], 0.08, 'sawtooth', 0.04);

interface Particle { id: number; left: number; delay: number; hue: number; drift: number; size: number; }
let particleSeq = 0;

// ============ LOBBY: host a session or join by code ============

const HillHistory: React.FC = () => {
  const [period, setPeriod] = useState<'day' | 'week' | 'month'>('day');
  const [buckets, setBuckets] = useState<HistoryBucket[]>([]);
  const [error, setError] = useState('');
  const [target, setTarget] = useState(DEFAULT_TARGETS.day);

  useEffect(() => {
    const load = () =>
      fetch(`${API_BASE_URL}/api/power-hour/history?period=${period}&limit=12`)
        .then(r => r.json())
        .then(d => {
          if (d.error) setError(d.error);
          else {
            setError('');
            setBuckets(d.buckets || []);
            if (d.target) setTarget(d.target);
          }
        })
        .catch(e => setError(String(e)));
    load();
    const t = window.setInterval(load, 30000);
    return () => window.clearInterval(t);
  }, [period]);

  const label = (p: string) => {
    if (period === 'day') {
      const today = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });
      return p === today ? 'TODAY' : new Date(p + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    }
    return p;
  };

  return (
    <div className="ph-history-card">
      <div className="ph-history-head">
        <span className="ph-hill-title">⛰️ THE HILL — HISTORY</span>
        <div className="ph-history-controls">
          {(['day', 'week', 'month'] as const).map(p => (
            <button key={p} className={`ph-period-btn ${period === p ? 'active' : ''}`}
              onClick={() => setPeriod(p)}>{p.toUpperCase()}</button>
          ))}
          <span className="ph-target-readonly">🎯 {target}</span>
        </div>
      </div>
      {error && <div className="ph-join-error">History unavailable — run the setup script. ({error.slice(0, 80)})</div>}
      {!error && buckets.length === 0 && <div className="ph-admin-hint">No calls recorded yet.</div>}

      {/* current-period leaderboard: outcomes up front */}
      {buckets[0] && (
        <div className="ph-period-board">
          <div className="ph-period-board-title">
            {period === 'day' ? "TODAY'S" : period === 'week' ? 'THIS WEEK’S' : 'THIS MONTH’S'} MEETING LEADERS
          </div>
          {buckets[0].reps.map((r, i) => (
            <div key={r.name} className={`ph-period-rep ${i === 0 && r.meetings > 0 ? 'leader' : ''}`}>
              <span className="ph-period-rank">{r.meetings > 0 ? (MEDALS[i] || i + 1) : '·'}</span>
              <span className="ph-period-name">
                <span className="ph-avatar" style={{ background: `hsl(${repHue(r.name)}, 70%, 45%)` }}>{initials(r.name)}</span>
                {r.name}
              </span>
              <span className="ph-period-mtgs">{r.meetings > 0 ? <>{'⭐'.repeat(Math.min(r.meetings, 4))} <b>{r.meetings}</b></> : <span className="ph-mtg-zero">0</span>}</span>
              <span className="ph-period-sub">{r.dials} dials · {r.connects} connects</span>
              <span className="ph-period-coins">🪙 {r.coins.toLocaleString()}</span>
            </div>
          ))}
        </div>
      )}

      {buckets.map(b => {
        const pct = Math.min(1, b.meetings / target);
        const over = b.meetings - target;
        const top = b.reps[0];
        return (
          <div key={b.period} className="ph-history-row">
            <span className="ph-history-period">{label(b.period)}</span>
            <div className="ph-history-bar">
              <div className="ph-history-fill" style={{ width: `${pct * 100}%` }} />
              <span className="ph-history-climber" style={{ left: `calc(${pct * 100}% - 10px)` }}>
                {over >= 0 ? (over > 0 ? '🚀' : '🏆') : '🧗'}
              </span>
            </div>
            <span className="ph-history-meetings">{b.meetings}<em>/{target}{over > 0 ? ` +${over}` : ''}</em></span>
            <span className="ph-history-stats">{b.dials} dials · {b.connects} connects</span>
            <span className="ph-history-top">{top ? `👑 ${top.name}` : '—'}</span>
          </div>
        );
      })}
    </div>
  );
};

const PowerHourLobby: React.FC = () => {
  const navigate = useNavigate();
  const [active, setActive] = useState<LobbySession[]>([]);
  const [posting, setPosting] = useState(false);

  const [formName, setFormName] = useState('POWER HOUR');
  const [formStart, setFormStart] = useState(() => {
    const d = new Date();
    d.setSeconds(0, 0);
    return toLocalInput(d);
  });
  const [formDuration, setFormDuration] = useState(60);
  const [formTarget, setFormTarget] = useState(5);

  useEffect(() => {
    const load = () =>
      fetch(`${API_BASE_URL}/api/power-hour/sessions`)
        .then(r => r.json())
        .then(d => setActive(d.sessions || []))
        .catch(() => {});
    load();
    const t = window.setInterval(load, 10000);
    return () => window.clearInterval(t);
  }, []);

  const host = async () => {
    setPosting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/power-hour/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formName,
          start_time: new Date(formStart).toISOString(),
          duration_minutes: formDuration,
          meeting_target: formTarget,
        }),
      });
      const d = await res.json();
      if (d.code && d.admin_key) {
        localStorage.setItem(`ph-admin-${d.code}`, d.admin_key);
        navigate(`/power-hour/${d.code}`);
      }
    } finally {
      setPosting(false);
    }
  };

  const join = (code: string) => navigate(`/power-hour/${code.trim().toUpperCase()}`);

  return (
    <div className="ph-page ph-setup-phase">
      <div className="ph-topbar">
        <div className="ph-logo"><FiZap /> EARN THE SUMMIT</div>
        <div className="ph-lobby-nav">
          <button className="ph-mini-cta" onClick={() => navigate('/power-hour/tv')}>📺 TV MODE</button>
          <button className="ph-mini-cta" onClick={() => navigate('/power-hour/coins')}>🪙 THE VAULT</button>
          <button className="ph-mini-cta" onClick={() => navigate('/power-hour/config')}>⚙️ CONFIG</button>
          <button className="ph-mini-cta" onClick={() => navigate('/power-hour/guide')}>❓ GUIDE</button>
        </div>
      </div>
      <div className="ph-lobby">
        <div className="ph-setup-card">
          <h2><FiZap /> HOST A POWER HOUR</h2>
          <p className="ph-setup-sub">You run the board: set the block, award the meetings.</p>
          <label>Session name
            <input value={formName} onChange={e => setFormName(e.target.value)} />
          </label>
          <label>Starts at
            <input type="datetime-local" value={formStart} onChange={e => setFormStart(e.target.value)} />
          </label>
          <div className="ph-setup-row">
            <label>Duration (min, 0 = no timer)
              <input type="number" min={0} max={1440} value={formDuration}
                onChange={e => setFormDuration(parseInt(e.target.value) || 0)} />
            </label>
            <label>Meeting target 🎯
              <input type="number" min={1} max={100} value={formTarget}
                onChange={e => setFormTarget(parseInt(e.target.value) || 5)} />
            </label>
          </div>
          <button className="ph-cta" onClick={host} disabled={posting}>
            {posting ? 'ARMING…' : '🔥 LIGHT IT UP'}
          </button>
        </div>

        <div className="ph-setup-card">
          <h2><FiLogIn /> JOIN THE FLOOR</h2>
          <p className="ph-setup-sub">Headset on — the floor is in Nooks. Boards update by themselves.</p>
          <a
            className="ph-cta ph-cta-nooks"
            href="https://app.nooks.in/workspaces/aXa7JlejBzJOXWFf"
            target="_blank"
            rel="noopener noreferrer"
          >
            📞 OPEN NOOKS — START DIALING
          </a>
          <div className="ph-lobby-list">
            <div className="ph-admin-label">{active.length ? 'LIVE NOW' : 'No sessions running — start one!'}</div>
            {active.map(s => (
              <button key={s.code} className="ph-lobby-session" onClick={() => join(s.code)}>
                <span className="ph-lobby-code">{s.code}</span>
                <span className="ph-lobby-name">{s.name}</span>
                <span className="ph-lobby-meta">🎯 {s.meeting_target} · {s.duration_minutes}m</span>
              </button>
            ))}
          </div>
        </div>
      </div>
      <HillHistory />
    </div>
  );
};

// ============ BOARD: the live session ============

const PowerHourBoard: React.FC<{ code: string }> = ({ code }) => {
  const navigate = useNavigate();
  const adminKey = adminKeyFor(code);
  const isAdmin = !!adminKey;

  const [data, setData] = useState<LivePayload | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [showAdmin, setShowAdmin] = useState(false);
  const [sound, setSound] = useState(false);
  const [particles, setParticles] = useState<Particle[]>([]);
  const [banner, setBanner] = useState<string | null>(null);
  const [shake, setShake] = useState(false);

  // host time-slot editor (prefilled from the live session when drawer opens)
  const [editStart, setEditStart] = useState('');
  const [editDuration, setEditDuration] = useState(60);
  const [editTarget, setEditTarget] = useState(5);
  const [saving, setSaving] = useState(false);

  const prevRef = useRef<LivePayload | null>(null);
  const soundRef = useRef(sound);
  soundRef.current = sound;
  const pageRef = useRef<HTMLDivElement>(null);
  const hillPathRef = useRef<SVGPathElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const confettiBurst = useCallback((count = 70) => {
    const burst: Particle[] = Array.from({ length: count }, () => ({
      id: ++particleSeq,
      left: Math.random() * 100,
      delay: Math.random() * 0.6,
      hue: Math.floor(Math.random() * 360),
      drift: (Math.random() - 0.5) * 200,
      size: 6 + Math.random() * 8,
    }));
    setParticles(p => [...p, ...burst]);
    window.setTimeout(() => {
      setParticles(p => p.filter(x => !burst.includes(x)));
    }, 3500);
  }, []);

  const fetchLive = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/power-hour/live?code=${encodeURIComponent(code)}`);
      if (res.status === 404) {
        setNotFound(true);
        return;
      }
      const payload: LivePayload = await res.json();
      const prev = prevRef.current;

      if (prev && payload.session) {
        const prevByName = new Map(prev.reps.map(r => [r.name, r]));
        const prevRank = new Map(prev.reps.map((r, i) => [r.name, i]));
        let meetingRep: string | null = null;
        let coinsUp = false;
        let rankUp = false;
        payload.reps.forEach((r, i) => {
          const p = prevByName.get(r.name);
          if (p) {
            if (r.meetings > p.meetings) meetingRep = r.name;
            if (r.coins > p.coins) coinsUp = true;
            const pr = prevRank.get(r.name);
            if (pr !== undefined && i < pr) rankUp = true;
          } else if (r.coins > 0) {
            coinsUp = true;
          }
        });
        if (meetingRep) {
          confettiBurst(140);
          setBanner(`🚀 ${meetingRep} BOOKED A MEETING! +500 🪙`);
          setShake(true);
          window.setTimeout(() => setShake(false), 900);
          window.setTimeout(() => setBanner(null), 6000);
          if (soundRef.current) sndMeeting();
        } else if (rankUp) {
          if (soundRef.current) sndRankUp();
        } else if (coinsUp) {
          if (soundRef.current) sndCoin();
        }
      }
      prevRef.current = payload;
      setData(payload);
    } catch { /* keep last board on transient errors */ }
  }, [code, confettiBurst]);

  useEffect(() => {
    fetchLive();
    const poll = window.setInterval(fetchLive, POLL_MS);
    const tick = window.setInterval(() => setNow(Date.now()), 250);
    const onFs = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFs);
    return () => {
      window.clearInterval(poll);
      window.clearInterval(tick);
      document.removeEventListener('fullscreenchange', onFs);
    };
  }, [fetchLive]);

  const session = data?.session || null;
  const startMs = session ? new Date(session.start_time).getTime() : 0;
  const openEnded = !!session && session.duration_minutes <= 0;
  const endMs = session && !openEnded ? startMs + session.duration_minutes * 60000 : Infinity;
  const phase: 'loading' | 'pre' | 'live' | 'done' =
    !session ? 'loading' : now < startMs ? 'pre' : now < endMs ? 'live' : 'done';

  const team = data?.team || { dials: 0, connects: 0, meetings: 0, coins: 0 };
  const target = session?.meeting_target || 1;
  const rawProgress = team.meetings / target;       // uncapped — can exceed 1
  const progress = Math.min(1, rawProgress);        // capped for the bar/summit
  const overTarget = Math.max(0, team.meetings - target);

  // climber position along the hill path; past the summit it keeps climbing
  // into the sky toward the star (summit 350,50 -> star 388,16)
  const [climber, setClimber] = useState({ x: 60, y: 250 });
  useEffect(() => {
    const path = hillPathRef.current;
    if (!path) return;
    if (rawProgress <= 1) {
      const len = path.getTotalLength();
      const pt = path.getPointAtLength(len * Math.min(1, rawProgress));
      setClimber({ x: pt.x, y: pt.y });
    } else {
      // each meeting past target is 20% of the sky leg, capped at the star
      const frac = Math.min(1, (rawProgress - 1) / 0.5);
      setClimber({ x: 350 + 38 * frac, y: 50 - 34 * frac });
    }
  }, [rawProgress, phase]);

  // celebrate hitting the target once
  const targetHitRef = useRef(false);
  useEffect(() => {
    if (progress >= 1 && !targetHitRef.current && phase === 'live') {
      targetHitRef.current = true;
      confettiBurst(160);
      setBanner('🏔️ SUMMIT REACHED — TARGET CRUSHED! 🏆');
      if (soundRef.current) sndMeeting();
    }
    if (progress < 1) targetHitRef.current = false;
  }, [progress, phase, confettiBurst]);

  const endSession = async () => {
    if (!window.confirm('End this Power Hour for everyone?')) return;
    await fetch(`${API_BASE_URL}/api/power-hour/session/${code}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ admin_key: adminKey }),
    });
    localStorage.removeItem(`ph-admin-${code.toUpperCase()}`);
    navigate('/power-hour');
  };

  const openAdmin = () => {
    if (data?.session) {
      setEditStart(toLocalInput(new Date(data.session.start_time)));
      setEditDuration(data.session.duration_minutes);
      setEditTarget(data.session.meeting_target);
    }
    setShowAdmin(s => !s);
  };

  const saveTimeSlot = async () => {
    setSaving(true);
    try {
      await fetch(`${API_BASE_URL}/api/power-hour/session/${code}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          admin_key: adminKey,
          start_time: new Date(editStart).toISOString(),
          duration_minutes: editDuration,
          meeting_target: editTarget,
        }),
      });
      prevRef.current = null; // window changed: don't fire celebrations on the re-pull
      await fetchLive();
    } finally {
      setSaving(false);
    }
  };

  const toggleFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else pageRef.current?.requestFullscreen();
  };

  const tickerItems = useMemo(() => {
    const evts = (data?.events || [])
      .slice()
      .reverse()
      .map(e => `🪙 ${e.rep} BOOKED A MEETING`);
    const mix: string[] = [];
    const max = Math.max(evts.length, HYPE.length);
    for (let i = 0; i < max; i++) {
      if (evts[i]) mix.push(evts[i]);
      mix.push(HYPE[i % HYPE.length]);
    }
    return mix;
  }, [data]);

  const leader = data?.reps[0];

  if (notFound) {
    return (
      <div className="ph-page">
        <div className="ph-countdown-stage">
          <div className="ph-countdown-label">SESSION OVER / NOT FOUND</div>
          <div className="ph-countdown-hint">Code "{code.toUpperCase()}" isn't live anymore.</div>
          <button className="ph-cta ph-cta-narrow" onClick={() => navigate('/power-hour')}>BACK TO LOBBY</button>
        </div>
      </div>
    );
  }

  return (
    <div className={`ph-page ph-${phase} ${shake ? 'ph-shake' : ''}`} ref={pageRef}>
      {/* confetti layer */}
      <div className="ph-confetti">
        {particles.map(p => (
          <span
            key={p.id}
            className="ph-particle"
            style={{
              left: `${p.left}%`,
              animationDelay: `${p.delay}s`,
              background: `hsl(${p.hue}, 95%, 60%)`,
              width: p.size,
              height: p.size * 0.45,
              ['--drift' as any]: `${p.drift}px`,
            }}
          />
        ))}
      </div>

      {banner && <div className="ph-banner">{banner}</div>}

      {/* top bar */}
      <div className="ph-topbar">
        <div className="ph-logo"><FiZap /> {session?.name || 'POWER HOUR'}</div>
        <div className="ph-join-badge" title="Share this code — anyone can join at /power-hour">
          JOIN CODE <b>{code.toUpperCase()}</b>
          {isAdmin && <span className="ph-host-tag">HOST</span>}
          {data?.sources && (() => {
            const s = data.sources;
            const fresh = s.webhook_last_secs != null && s.webhook_last_secs < 1800;
            const cls = s.nooks_calls > 0 || fresh ? 'live' : s.nooks_webhook_configured ? 'armed' : 'gong';
            const title = s.nooks_calls > 0 || fresh
              ? `🟢 Nooks realtime — last delivery ${s.webhook_last_secs != null ? Math.round(s.webhook_last_secs / 60) : '?'}m ago (${s.nooks_calls} in window, ${s.gong_calls} via Gong)`
              : s.nooks_webhook_configured
              ? '🟡 Webhook armed — no deliveries in the last 30 min (quiet floor or tunnel down)'
              : '🔵 Gong only — wire up the Nooks webhook for realtime';
            return <span className={`ph-source-dot ${cls}`} title={title}>●</span>;
          })()}
        </div>
        <div className="ph-topbar-actions">
          <button className="ph-icon-btn" onClick={() => navigate('/power-hour')} title="Back to lobby">
            <FiArrowLeft />
          </button>
          <button className="ph-icon-btn" onClick={() => setSound(s => !s)} title="Sound">
            {sound ? <FiVolume2 /> : <FiVolumeX />}
          </button>
          {isAdmin && (
            <button className="ph-icon-btn" onClick={openAdmin} title="Host controls">
              <FiSettings />
            </button>
          )}
          <button className="ph-icon-btn" onClick={toggleFullscreen} title="Fullscreen (for the big screen!)">
            {isFullscreen ? <FiMinimize /> : <FiMaximize />}
          </button>
        </div>
      </div>

      {phase === 'loading' && (
        <div className="ph-countdown-stage">
          <div className="ph-countdown-label">LOADING THE BOARD…</div>
        </div>
      )}

      {phase === 'pre' && session && (
        <div className="ph-countdown-stage">
          <div className="ph-countdown-label">DIALING BEGINS IN</div>
          <div className="ph-countdown-clock">{fmtClock(startMs - now)}</div>
          <div className="ph-countdown-target">
            🎯 TARGET: <b>{session.meeting_target}</b> MEETINGS IN {session.duration_minutes} MIN
          </div>
          <div className="ph-countdown-hint">Headsets on. Leads loaded. Join code: <b>{code.toUpperCase()}</b> 🎧</div>
        </div>
      )}

      {(phase === 'live' || phase === 'done') && session && (
        <>
          {/* HUD: clock + target ticker */}
          <div className="ph-hud">
            <div className="ph-hud-clock">
              <div className="ph-hud-label">
                {phase !== 'live' ? '🏁 FINAL' : openEnded ? '⏱ ELAPSED' : '⏱ TIME LEFT'}
              </div>
              <div className={`ph-hud-time ${phase === 'live' && !openEnded && endMs - now < 5 * 60000 ? 'urgent' : ''}`}>
                {phase !== 'live' ? '00:00' : openEnded ? fmtClock(now - startMs) : fmtClock(endMs - now)}
              </div>
            </div>
            <div className="ph-hud-target">
              <div className="ph-hud-label">🎯 MEETINGS BOOKED</div>
              <div className="ph-hud-score">
                <span className="ph-hud-big">{team.meetings}</span>
                <span className="ph-hud-sep">/</span>
                <span>{target}</span>
              </div>
              <div className="ph-progressbar">
                <div className="ph-progressfill" style={{ width: `${progress * 100}%` }} />
              </div>
            </div>
            <div className="ph-hud-stats">
              <div className="ph-stat"><span>{team.dials}</span>DIALS</div>
              <div className="ph-stat"><span>{team.connects}</span>CONNECTS</div>
              <div className="ph-stat ph-stat-coins"><span>🪙 {team.coins.toLocaleString()}</span>TEAM COINS</div>
            </div>
          </div>

          <div className="ph-main">
            {/* leaderboard */}
            <div className="ph-board">
              <div className="ph-board-head">
                <span className="ph-col-rank">#</span>
                <span className="ph-col-name">REP</span>
                <span className="ph-col-mtg-hero">🎯 MEETINGS</span>
                <span className="ph-col-num">DIALS</span>
                <span className="ph-col-num">CONNECTS</span>
                <span className="ph-col-coins">COINS</span>
              </div>
              <div className="ph-rows" style={{ height: Math.max(1, data!.reps.length) * ROW_H }}>
                {data!.reps.length === 0 && (
                  <div className="ph-empty">Waiting for the first dial… ☎️</div>
                )}
                {data!.reps.map((r, i) => (
                  <div
                    key={r.name}
                    className={`ph-row ${i === 0 ? 'ph-row-leader' : ''}`}
                    style={{ transform: `translateY(${i * ROW_H}px)` }}
                  >
                    <span className="ph-col-rank">{MEDALS[i] || i + 1}</span>
                    <span className="ph-col-name">
                      <span className="ph-avatar" style={{ background: `hsl(${repHue(r.name)}, 70%, 45%)` }}>
                        {initials(r.name)}
                      </span>
                      {r.name}
                      {i === 0 && <span className="ph-crown">👑</span>}
                      {(r.streak || 0) >= 2 && <span className="ph-streak">🔥{r.streak}</span>}
                    </span>
                    <span className={`ph-col-mtg-hero ${r.meetings > 0 ? 'has-mtgs' : ''}`}>
                      {r.meetings > 0
                        ? <>{'⭐'.repeat(Math.min(r.meetings, 4))} <b>{r.meetings}</b></>
                        : <span className="ph-mtg-zero">–</span>}
                    </span>
                    <span className="ph-col-num">{r.dials}</span>
                    <span className="ph-col-num">{r.connects}</span>
                    <span className="ph-col-coins">🪙 {r.coins.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* hill climb */}
            <div className="ph-hill-card">
              <div className="ph-hill-title">
                {overTarget > 0 ? `🚀 BEYOND THE SUMMIT — +${overTarget} OVER TARGET` : `⛰️ ROAD TO ${target}`}
              </div>
              <svg viewBox="0 0 400 280" className="ph-hill" preserveAspectRatio="xMidYMax meet">
                <defs>
                  <linearGradient id="phSky" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#1a0b3d" />
                    <stop offset="100%" stopColor="#3b1265" />
                  </linearGradient>
                  <linearGradient id="phHill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#7c3aed" />
                    <stop offset="100%" stopColor="#2d1b4e" />
                  </linearGradient>
                </defs>
                <rect width="400" height="280" fill="url(#phSky)" rx="12" />
                <circle cx="340" cy="50" r="24" fill="#fbbf24" opacity="0.9">
                  <animate attributeName="opacity" values="0.7;1;0.7" dur="3s" repeatCount="indefinite" />
                </circle>
                {[...Array(12)].map((_, i) => (
                  <circle key={i} cx={(i * 97 + 31) % 400} cy={(i * 53 + 17) % 110} r={i % 3 === 0 ? 2 : 1.2} fill="#fff" opacity={0.6}>
                    <animate attributeName="opacity" values="0.2;0.9;0.2" dur={`${2 + (i % 4)}s`} repeatCount="indefinite" />
                  </circle>
                ))}
                {/* hill body */}
                <path d="M 0 280 L 0 250 C 90 245, 160 215, 230 160 C 290 113, 330 75, 352 48 L 400 48 L 400 280 Z" fill="url(#phHill)" />
                {/* the climb path the marker follows */}
                <path
                  ref={hillPathRef}
                  d="M 12 248 C 95 242, 162 212, 230 158 C 288 112, 328 76, 350 50"
                  fill="none" stroke="#fbbf24" strokeWidth="3" strokeDasharray="7 7" opacity="0.85"
                />
                {/* summit flag */}
                <g transform="translate(350, 50)">
                  <line x1="0" y1="0" x2="0" y2="-34" stroke="#e5e7eb" strokeWidth="3" />
                  <path d="M 0 -34 L 30 -26 L 0 -18 Z" fill="#ef4444">
                    <animateTransform attributeName="transform" type="skewY" values="0;4;0;-3;0" dur="1.6s" repeatCount="indefinite" />
                  </path>
                  <text x="4" y="-40" fill="#fbbf24" fontSize="15" fontWeight="bold">{target}</text>
                </g>
                {/* bonus star past the summit */}
                {overTarget > 0 && (
                  <text x="388" y="16" textAnchor="middle" fontSize="18">🌟
                    <animate attributeName="opacity" values="0.5;1;0.5" dur="1.2s" repeatCount="indefinite" />
                  </text>
                )}
                {/* climber */}
                <g className="ph-climber" transform={`translate(${climber.x}, ${climber.y})`}>
                  <circle r="13" fill="#fbbf24" stroke="#fff" strokeWidth="2.5" />
                  <text textAnchor="middle" dy="5" fontSize="14">{overTarget > 0 ? '🚀' : progress >= 1 ? '🏆' : '🧗'}</text>
                  <text textAnchor="middle" dy="-20" fill="#fff" fontSize="12" fontWeight="bold">{team.meetings}</text>
                </g>
              </svg>
              {leader && (
                <div className="ph-trophy-strip">
                  🏆 KING OF THE HILL: <b>{leader.name}</b> · 🪙 {leader.coins.toLocaleString()}
                </div>
              )}
            </div>
          </div>

          {/* ticker */}
          <div className="ph-ticker">
            <div className="ph-ticker-track">
              {[...tickerItems, ...tickerItems].map((t, i) => (
                <span key={i} className="ph-ticker-item">{t}<em>◆</em></span>
              ))}
            </div>
          </div>

          {phase === 'done' && (
            <div className="ph-final-overlay">
              <div className="ph-final-card">
                <h2>🏁 TIME! FINAL BOARD</h2>
                {leader ? (
                  <p>🏆 <b>{leader.name}</b> takes the day with 🪙 {leader.coins.toLocaleString()}!</p>
                ) : (
                  <p>No dials logged this block.</p>
                )}
                <p>{team.meetings >= target ? '🎯 TARGET HIT — drinks on the manager.' : `Team booked ${team.meetings}/${target}. Run it back. 🔁`}</p>
                {isAdmin ? (
                  <button className="ph-cta" onClick={endSession}>NEW POWER HOUR</button>
                ) : (
                  <button className="ph-cta" onClick={() => navigate('/power-hour')}>BACK TO LOBBY</button>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* host / moderator drawer */}
      {showAdmin && isAdmin && (
        <div className="ph-admin">
          <div className="ph-admin-head">
            <span>HOST CONTROLS</span>
            <button className="ph-icon-btn" onClick={() => setShowAdmin(false)}><FiX /></button>
          </div>
          <div className="ph-admin-section">
            <div className="ph-admin-label">Time slot (back-date to pull earlier Gong calls)</div>
            <input
              type="datetime-local"
              value={editStart}
              onChange={e => setEditStart(e.target.value)}
            />
            <div className="ph-admin-btns">
              <input
                type="number" min={0} max={1440} value={editDuration}
                title="Duration (minutes, 0 = no timer)"
                onChange={e => setEditDuration(parseInt(e.target.value) || 0)}
              />
              <input
                type="number" min={1} max={100} value={editTarget}
                title="Meeting target"
                onChange={e => setEditTarget(parseInt(e.target.value) || 5)}
              />
              <button className="ph-mini-cta" onClick={saveTimeSlot} disabled={saving}>
                {saving ? '…' : 'Save'}
              </button>
            </div>
            <div className="ph-admin-hint">duration (min, 0 = no timer) · target 🎯 — e.g. back-date to 9:00 AM and the board fills with everything since.</div>
          </div>
          <div className="ph-admin-section">
            <button className="ph-mini-cta ph-mini-danger" onClick={endSession}>End session for everyone</button>
          </div>
          {data?.gong_error && <div className="ph-admin-err">Gong: {data.gong_error}</div>}
        </div>
      )}
    </div>
  );
};

const PowerHourPage: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  if (!code) return <PowerHourLobby />;
  return <PowerHourBoard key={code} code={code} />;
};

export default PowerHourPage;
